import DesignTokens from '@tableau/design-tokens';
import { ArrowRightIcon } from '@tableau/icons/ArrowRightIcon';
import { PencilBaseIcon } from '@tableau/icons/PencilBaseIcon';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { EditRemoteAgentPoolDialogBody, EditRemoteAgentPoolDialogProps, EditRemoteAgentPoolDialogState } from '~src/settings/bridge-settings/sections/bridgeRemoteAgents/EditRemoteAgentPoolDialog';
import { IUpdateEdgePoolResult } from '~src/settings/bridge-settings/serverApi/requests/UpdateEdgePoolRequest';
import { EdgePool } from '~src/settings/bridge-settings/serverApi/resources/EdgePool';
import { FieldArrayRenderProps, FormikContextType } from '~src/settings/FormikWrapperForStubbing';
import { IRemoteQueryAgent } from '~src/settings/types/ISiteAdminBridgeSettings';
import { PageHeaderStyles } from '~src/spacesPlatform/components/layout/PageHeaderStyles';
import { applyColor } from '~src/style/ColorService';

import { SiteBridgeSettingsForSiteAdminProps } from '../../BridgeSiteSettings';
import { PoolAssignmentOption } from '../types/PoolAssignmentOption';
import { PoolID, SystemDefinedPoolID } from '../types/PoolID';
import { getErrorMessage } from '../utils/EdgePoolErrorUtilities';

export type BridgeRemoteAgentsEditButtonProps = Readonly<{
  agent: IRemoteQueryAgent;
  index: number;
  arrayHelpers: FieldArrayRenderProps;
  formik: FormikContextType<SiteBridgeSettingsForSiteAdminProps>;
  isDefaultPool: boolean;
  poolTestId: string;
  poolOptions: ReadonlyArray<PoolAssignmentOption>;
  siteId: string;
  agentPoolId: PoolID;
  refreshEdgePools: () => void;
}>;

export function BridgeRemoteAgentsEditButton(props: BridgeRemoteAgentsEditButtonProps) {
  return <>
    {FeatureFlags.edgeMultiPoolEnabled ?
      <span className={'tb-data-grid-icon tb-padded-left-large'} data-tb-test-id={'agent-edit-cell' + props.poolTestId}>
        {renderEditRemoteAgentButton(props)}
      </span>
      : null
    }
  </>;
}


function renderEditRemoteAgentButton(props: BridgeRemoteAgentsEditButtonProps) {
  const { isDefaultPool, poolTestId } = props;
  return (
    <ButtonWidget
      title={Messages.edit()}
      buttonType={isDefaultPool ? ButtonType.Icon : ButtonType.LowEmphasis}
      testId={'edit-remote-agent-button' + poolTestId}
      onClick={() => openEditRemoteAgentDialog(props)}
      style={{ alignItems: 'stretch' }}
    >
      {isDefaultPool ?
        <PencilBaseIcon
          size={14}
          alt={Messages.edit()}
          color={applyColor(DesignTokens.color.font.default.secondary, '#666')}
          style={PageHeaderStyles.iconForTextButton}
        />
        :
        <span>
          <ArrowRightIcon
            size={14}
            alt={Messages.edit()}
            color={applyColor(DesignTokens.color.font.default.secondary, '#666')}
            style={PageHeaderStyles.iconForTextButton}
          />
          {!isDefaultPool && Messages.siteRemoteAgentSettingsAssignToPool()}
        </span>
      }
    </ButtonWidget>
  );
}

function openEditRemoteAgentDialog(props: BridgeRemoteAgentsEditButtonProps) {
  const { agent, index, arrayHelpers, formik, poolOptions, siteId } = props;
  const action = FeatureFlags.edgeMultiPoolEnabled ?
    // Use Edge UpdatePool API to update agents
    (_: EditRemoteAgentPoolDialogProps, state: EditRemoteAgentPoolDialogState) => {
      EdgePool.updatePool({
        poolId: state.selectedPoolId,
        displayName: null,
        domainChange: null,
        agentChange: {agentId: agent.id},
        siteId: siteId,
        bridgeSettingsVersion: '0'
      }).then((result: IUpdateEdgePoolResult) => {
        props.refreshEdgePools();
        if (result.success) {
          arrayHelpers.replace(index, { ...agent, agentPoolId: state.selectedPoolId });
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.editRemoteAgentPoolSuccess() });
        }
        if (result.error) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getErrorMessage(result.error.errorCode, {poolName: null, domainName: null, agentName: agent.agentName})
          });
        }
      });
      return Promise.resolve();
    }
    :
    // Use formik to update agents
    (_: EditRemoteAgentPoolDialogProps, state: EditRemoteAgentPoolDialogState) => {
      arrayHelpers.replace(index, { ...agent, pooled: state.selectedPoolId === SystemDefinedPoolID.Default });
      ToasterService.instance.pop({ type: ToastType.Success, text: Messages.editRemoteAgentPoolSuccess() });
      return formik.submitForm();
    };

  const dialogInstance = openReactActionDialog({
    title: Messages.editRemoteAgentPoolDialogTitle(),
    bodyComponent: EditRemoteAgentPoolDialogBody,
    initialBodyProps: { agent, poolOptions },
    initialDialogState: { selectedPoolId: props.agentPoolId },
    actionName: Messages.save(),
    action: action,
    actionDisabled: (_, state) => !state.selectedPoolId,
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width375px,
  });

  return Promise.resolve(dialogInstance.result);
}
